import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { format, addDays, startOfDay, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isBefore, startOfToday } from 'date-fns';
import { Calendar, Clock, User, Mail, ChevronLeft, ChevronRight, Plus, X, CheckCircle, AlertCircle, LogOut, Settings, Info, FileText, Bell, Home, ExternalLink, Send, Trash2, Search, Menu, ArrowRight, Play, BarChart3, MessageSquare, ChevronDown, ChevronUp, Award } from 'lucide-react';
import { apiGet, apiPost, convertLocalTimeToEpoch, convertEpochToLocalTime, convertEpochToLocalDate, formatTimeRange, calculateDuration, getUserTimezone, apiPut } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import cache from '../../utils/cache';
import PatientTasks from '../tasks/PatientTasks';
import DynamicHeader from '../common/DynamicHeader';
import TaskPage from '../tasks/TaskPage';
import { getTaskById } from '../../config/taskRegistry';
import { Button, Card, Input, Select, Badge, Alert, Navigation, NavigationItem, UserHeader, CloseButton, Divider, Spinner } from '../design-system';
import NotificationPromptModal from '../notifications/NotificationPromptModal';
import { MoodTracker, MoodHistoryModal, MoodAnalyticsSummary } from '../mood-tracker';
import GroundingQuickAccess from '../grounding/GroundingQuickAccess';
import { GroundingAnalytics } from '../grounding/GroundingAnalytics';
import MascotHub from './MascotHub';
import moodTrackerService from '../../services/moodTrackerService';
import groundingService from '../../services/groundingService';
import therapeuticGoalsService from '../../services/therapeuticGoalsService';
import CardCollectionView from '../cards/CardCollectionView';

// Constants moved outside component to prevent recreation on every render
const TIME_OPTIONS = (() => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      const timeString = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push(timeString);
    }
  }
  return options;
})();

const MENU_ITEMS = [
  { id: 'dashboard', name: 'Dashboard', icon: Home, active: true },
  { id: 'appointments', name: 'Appointments', icon: Calendar },
  { id: 'tasks', name: 'My Tasks', icon: FileText },
  { id: 'analytics', name: 'Analytics', icon: BarChart3 },
  { id: 'therapist', name: 'About Therapist', icon: User },
  { id: 'settings', name: 'Settings', icon: Settings },
  { id: 'notifications', name: 'Notifications', icon: Bell },
];

const PatientDashboard = () => {
  
  const { user, logout, userInfo } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [cameFromBookingModal, setCameFromBookingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [assignedTherapist, setAssignedTherapist] = useState(null);
  const [loadingTherapist, setLoadingTherapist] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [welcomeAnimationComplete, setWelcomeAnimationComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [activeAppointmentTab, setActiveAppointmentTab] = useState('upcoming');
  
  // New state for pending tasks
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  
  // State for goals
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [otherTasksExpanded, setOtherTasksExpanded] = useState(false); // Default to collapsed
  
  // State for grounding assignment
  const [groundingAssignment, setGroundingAssignment] = useState(null);
  const [loadingGrounding, setLoadingGrounding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpSection, setHelpSection] = useState('');
  const [previousPage, setPreviousPage] = useState('dashboard');
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasNotificationSubscription, setHasNotificationSubscription] = useState(false);
  const [showMoodTracker, setShowMoodTracker] = useState(false);
  const [autoShowMoodTracker, setAutoShowMoodTracker] = useState(false);
  const [showGrounding, setShowGrounding] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);
  const [patientName, setPatientName] = useState(null);
  const [moodAlreadySubmitted, setMoodAlreadySubmitted] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm();

  // Loading state guards to prevent duplicate API calls
  const loadingRef = useRef({
    therapist: false,
    bookings: false,
    tasks: false,
    slots: false,
    goals: false,
    mood: false,
    notifications: false,
    grounding: false
  });
  
  // Track initialization to prevent duplicate mount effects
  const hasInitializedRef = useRef(false);
  const moodCheckedRef = useRef(false);
  const previousActiveSectionRef = useRef(null);

  // Check notification status on mount
  useEffect(() => {
    if (!loadingRef.current.notifications) {
      checkInitialNotificationStatus();
    }
  }, []);

  // Check if mood was already submitted today - only once on mount
  useEffect(() => {
    const checkMoodStatus = async () => {
      // Prevent duplicate calls (especially from StrictMode)
      if (moodCheckedRef.current || loadingRef.current.mood || !user) {
        return;
      }
      
      moodCheckedRef.current = true;
      loadingRef.current.mood = true;
      
      try {
        const hasSubmitted = await moodTrackerService.isMoodSubmittedToday(user);
        setMoodAlreadySubmitted(hasSubmitted);
      } catch (error) {
        console.error('Error checking mood submission status:', error);
        setMoodAlreadySubmitted(false);
      } finally {
        loadingRef.current.mood = false;
      }
    };
    
    checkMoodStatus();
  }, [user]);

  const checkInitialNotificationStatus = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.notifications) {
      return;
    }
    
    loadingRef.current.notifications = true;
    
    try {
      // Check both browser permission AND user subscription
      const browserPermission = Notification.permission === "granted";
      
      if (browserPermission && 'serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        // Only set as enabled if BOTH browser permission AND subscription exist
        setHasNotificationSubscription(!!subscription);
      } else {
        // No browser permission = no notifications possible
        setHasNotificationSubscription(false);
      }
    } catch (error) {
      console.error('Error checking initial notification status:', error);
      setHasNotificationSubscription(false);
    } finally {
      loadingRef.current.notifications = false;
    }
  };

  // Use pre-computed time options
  const timeOptions = TIME_OPTIONS;

  // Watch form values for duration calculation
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  // Convert epoch to 12-hour format to match timeOptions - memoized
  const convertEpochTo12HourFormat = useCallback((epoch) => {
    const date = new Date(epoch * 1000);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  // Convert 12-hour format to 24-hour format for API calls - memoized
  const convert12HourTo24Hour = useCallback((time12Hour) => {
    const [time, period] = time12Hour.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes}`;
  }, []);

  // Auto-fill form when a slot is selected
  useEffect(() => {
    if (selectedSlot && showBookingForm) {
      const startTimeFormatted = convertEpochTo12HourFormat(selectedSlot.startEpoch);
      const endTimeFormatted = convertEpochTo12HourFormat(selectedSlot.endEpoch);
      
      // Small delay to ensure form is fully rendered
      setTimeout(() => {
        setValue('startTime', startTimeFormatted);
        setValue('endTime', endTimeFormatted);
      }, 100);
    }
  }, [selectedSlot, showBookingForm, setValue, convertEpochTo12HourFormat]);

  // Calculate duration when times change (handles 12-hour format) - memoized
  const calculateDurationFromTimes = useCallback((start, end) => {
    if (!start || !end) return 'Select start and end times to see duration';
    
    try {
      // Convert 12-hour format to 24-hour format for calculation
      const start24Hour = convert12HourTo24Hour(start);
      const end24Hour = convert12HourTo24Hour(end);
      
      const [startHour, startMinute] = start24Hour.split(':').map(Number);
      const [endHour, endMinute] = end24Hour.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMinute;
      let endMinutes = endHour * 60 + endMinute;
      
      // Handle overnight bookings (end time is next day)
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours
      }
      
      const durationMinutes = endMinutes - startMinutes;
      
      if (durationMinutes <= 0) {
        return 'End time must be after start time';
      }
      
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes} minutes`;
    } catch (error) {
      return 'Invalid time format';
    }
  }, [convert12HourTo24Hour]);

  // Use pre-computed menu items
  const menuItems = MENU_ITEMS;

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Other section components
  const AppointmentsContent = () => (
    <div className="space-y-4 md:space-y-6 pb-6">
      {/* My Appointments */}
      <Card variant="default" padding="large">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0 mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">My Appointments</h2>
          <div className="flex space-x-1 md:space-x-2 overflow-x-auto pb-2">
            <Button
              variant={activeAppointmentTab === 'upcoming' ? 'primary' : 'ghost'}
              size="small"
              onClick={() => setActiveAppointmentTab('upcoming')}
              className="flex-shrink-0"
            >
              Upcoming
            </Button>
            <Button
              variant={activeAppointmentTab === 'past' ? 'primary' : 'ghost'}
              size="small"
              onClick={() => setActiveAppointmentTab('past')}
              className="flex-shrink-0"
            >
              Past
            </Button>
          </div>
        </div>
        
        {/* Book Appointment Button */}
        <div className="mb-4">
          <Button
            variant="primary"
            size="medium"
            onClick={() => setShowBookingModal(true)}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Book Appointment
          </Button>
        </div>
        
        {(() => {
          const now = Math.floor(Date.now() / 1000);
          const filteredBookings = myBookings.filter(booking => {
            if (activeAppointmentTab === 'upcoming') {
              return booking.startEpoch > now;
            } else {
              return booking.startEpoch <= now;
            }
          });
          
          if (filteredBookings.length === 0) {
            return (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  {activeAppointmentTab === 'upcoming' ? 'No upcoming appointments' : 'No past appointments'}
                </p>
                {activeAppointmentTab === 'upcoming' && (
                  <Button
                    variant="outline"
                    size="medium"
                    onClick={() => setShowBookingModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Book Your First Appointment
                  </Button>
                )}
              </div>
            );
          }
          
          return (
            <div className="space-y-3">
              {filteredBookings.map((booking) => {
                const isUpcoming = booking.startEpoch > now;
                return (
                  <Card
                    key={booking._id}
                    variant={isUpcoming ? 'default' : 'outlined'}
                    padding="medium"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-2 sm:space-y-0">
                      <div className="flex items-start space-x-3">
                        <Clock className={`h-5 w-5 mt-0.5 ${
                          isUpcoming ? 'text-indigo-600' : 'text-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">
                            {convertEpochToLocalDate(booking.startEpoch)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {formatTimeRange(booking.startEpoch, booking.endEpoch)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Duration: {calculateDuration(booking.startEpoch, booking.endEpoch)}
                          </p>
                          {booking.message && (
                            <p className="text-sm text-gray-500 mt-1">
                              <MessageSquare className="h-4 w-4 inline mr-1" />
                              {booking.message}
                            </p>
                          )}
                          {booking.meetLink && isUpcoming && (
                            <div className="mt-2">
                              <a
                                href={booking.meetLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                Join Google Meet
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-left sm:text-right">
                        <Badge 
                          variant={isUpcoming ? 'success' : 'default'} 
                          size="small"
                        >
                          {isUpcoming ? 'Confirmed' : 'Completed'}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          );
        })()}
      </Card>
    </div>
  );

  // Analytics Content Component
  const AnalyticsContent = () => {
    const [showMoodHistoryModal, setShowMoodHistoryModal] = useState(false);

    return (
      <div className="space-y-4 md:space-y-6 pb-6">
        {/* Quick Stats */}
        <Card variant="default" padding="large">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card variant="default" padding="small" className="text-center">
              <Calendar className="h-5 w-5 text-indigo-600 mx-auto mb-1" />
              <div className="text-xl md:text-2xl font-bold text-indigo-600">{myBookings.length}</div>
              <div className="text-xs md:text-sm text-gray-600">Total Appointments</div>
            </Card>
            <Card variant="default" padding="small" className="text-center">
              <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <div className="text-xl md:text-2xl font-bold text-green-600">
                {pendingTasks.filter(task => task.status === 'completed').length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Completed Tasks</div>
            </Card>
            <Card variant="default" padding="small" className="text-center">
              <Clock className="h-5 w-5 text-orange-600 mx-auto mb-1" />
              <div className="text-xl md:text-2xl font-bold text-orange-600">
                {pendingTasks.filter(task => task.status === 'pending').length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Pending Tasks</div>
            </Card>
            <Card variant="default" padding="small" className="text-center">
              <Play className="h-5 w-5 text-blue-600 mx-auto mb-1" />
              <div className="text-xl md:text-2xl font-bold text-blue-600">
                {pendingTasks.filter(task => task.status === 'started').length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">In Progress</div>
            </Card>
          </div>
        </Card>

        {/* Mood Analytics */}
        <MoodAnalyticsSummary onViewDetails={() => setShowMoodHistoryModal(true)} />

        {/* Grounding Analytics */}
        <GroundingAnalytics />

        {/* Mood History Modal */}
        <MoodHistoryModal
          isOpen={showMoodHistoryModal}
          onClose={() => setShowMoodHistoryModal(false)}
          title="My Mood History"
        />
      </div>
    );
  };

  const TherapistInfoContent = () => (
    <Card variant="default" padding="large">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">About Your Therapist</h2>
      {assignedTherapist ? (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{assignedTherapist.name}</h3>
              <p className="text-gray-600">{assignedTherapist.email}</p>
              {assignedTherapist.specialization && (
                <p className="text-sm text-gray-500">{assignedTherapist.specialization}</p>
              )}
            </div>
          </div>
          <div className="pt-4">
            <Divider />
            <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
            <p className="text-sm text-gray-600">Email: {assignedTherapist.email}</p>
            {/* Add more therapist info here */}
          </div>
        </div>
      ) : (
        <p className="text-gray-600">Loading therapist information...</p>
      )}
    </Card>
  );

  const SettingsContent = () => (
    <Card variant="default" padding="large">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Settings</h2>
      <p className="text-gray-600">Coming soon: Account settings, preferences, and profile management.</p>
    </Card>
  );

  const NotificationsContent = () => (
    <div className="space-y-4">
      <Card variant="default" padding="large">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Push Notifications</h2>
        <NotificationManager 
          userId={userInfo?.userId} 
          onSubscriptionChange={(subscription) => {
            setHasNotificationSubscription(!!subscription);
          }}
        />
      </Card>
      
      <Card variant="default" padding="large">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Notification History</h2>
        <p className="text-gray-600">Coming soon: Notification history and preferences.</p>
      </Card>
    </div>
  );


  const HelpContent = () => (
    <Card variant="default" padding="large">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Help & Support</h2>
      <p className="text-gray-600">Coming soon: Help documentation, FAQs, and support contact information.</p>
    </Card>
  );

  const fetchAssignedTherapist = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.therapist) {
      return;
    }
    
    loadingRef.current.therapist = true;
    
    try {
      setLoadingTherapist(true);
      
      // Check cache first (24 hour TTL - therapist assignment rarely changes)
      const userId = userInfo?.userId;
      if (userId) {
        const cached = cache.get('therapist', userId);
        if (cached && cached.therapistInfo) {
          console.log('✅ Using cached therapist info');
          setAssignedTherapist(cached.therapistInfo);
          if (cached.patientInfo?.name) {
            setPatientName(cached.patientInfo.name);
          }
          setLoadingTherapist(false);
          loadingRef.current.therapist = false;
          checkInitialDataLoaded();
          return;
        }
      }
      
      // Fetch from server
      console.log('🌐 Fetching fresh therapist data from server...');
      const response = await apiGet('/.netlify/functions/get-patient-info');
      
      if (response.hasTherapist && response.therapistInfo) {
        setAssignedTherapist(response.therapistInfo);
        
        // Cache the result (24 hour TTL)
        if (userId) {
          cache.set('therapist', {
            therapistInfo: response.therapistInfo,
            patientInfo: response.patientInfo
          }, 24 * 60, userId); // 24 hours
          console.log('💾 Cached therapist info');
        }
      } else {
        toast.error('No therapist assigned. Please contact your administrator.');
      }
      
      // Get patient name from response
      if (response.patientInfo?.name) {
        setPatientName(response.patientInfo.name);
      }
    } catch (error) {
      console.error('Error fetching assigned therapist:', error);
      toast.error('Failed to load therapist information');
    } finally {
      setLoadingTherapist(false);
      loadingRef.current.therapist = false;
      checkInitialDataLoaded();
    }
  };

  const fetchAvailableSlots = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.slots || !assignedTherapist) {
      return;
    }
    
    loadingRef.current.slots = true;
    
    try {
      setLoading(true);
      
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      

      
      // Calculate day boundaries in local timezone
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      const startEpoch = Math.floor(startOfDay.getTime() / 1000);
      const endEpoch = Math.floor(endOfDay.getTime() / 1000);
      

      
      // Get all events for the therapist (no date filtering)
      const response = await apiGet(`/.netlify/functions/get-available-slots?therapistId=${assignedTherapist.userId}`);
      
      // Generate slots for the selected date using the week events
      const availableSlots = generateSlotsForDay(response.weekEvents || [], selectedDate);
      
      setAvailableSlots(availableSlots || []);
      
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
      loadingRef.current.slots = false;
    }
  };

  // Helper function to generate slots for a specific day (same logic as PublicBooking)
  const generateSlotsForDay = (events, targetDate) => {
    // Convert target date to viewer's local timezone boundaries
    const dayStart = new Date(format(targetDate, 'yyyy-MM-dd') + 'T00:00:00');
    const dayEnd = new Date(format(targetDate, 'yyyy-MM-dd') + 'T23:59:59');
    const dayStartEpoch = Math.floor(dayStart.getTime() / 1000);
    const dayEndEpoch = Math.floor(dayEnd.getTime() / 1000);

    // Convert all events to local time and find those that fall on the target date
    const relevantEvents = events.filter(event => {
      const eventStartDate = new Date(event.startEpoch * 1000);
      const eventEndDate = new Date(event.endEpoch * 1000);
      
      // Check if any part of this event falls on the target date in local time
      const targetDateStart = new Date(format(targetDate, 'yyyy-MM-dd') + 'T00:00:00');
      const targetDateEnd = new Date(format(targetDate, 'yyyy-MM-dd') + 'T23:59:59');
      
      return (eventStartDate < targetDateEnd && eventEndDate > targetDateStart);
    });



    // Separate availability windows and booked/blocked times
    const availabilityWindows = relevantEvents.filter(event => event.type === 'available');
    const bookedTimes = relevantEvents.filter(event => event.type === 'booked');
    const blockedTimes = relevantEvents.filter(event => event.type === 'blocked');

    // Generate 30-minute slots for the target date in local time
    const slots = [];
    for (let epoch = dayStartEpoch; epoch < dayEndEpoch; epoch += 1800) {
      const slotEndEpoch = epoch + 1800;
      
      // Check if this slot is available
      const isAvailable = availabilityWindows.some(event => 
        epoch >= event.startEpoch && 
        slotEndEpoch <= event.endEpoch
      );

      const isBooked = bookedTimes.some(event =>
        epoch < event.endEpoch && 
        slotEndEpoch > event.startEpoch
      );

      const isBlocked = blockedTimes.some(event =>
        epoch < event.endEpoch && 
        slotEndEpoch > event.startEpoch
      );

      if (isAvailable && !isBooked && !isBlocked) {
        slots.push({
          startEpoch: epoch,
          endEpoch: slotEndEpoch
        });
      }
    }

    return slots;
  };

  const fetchMyBookings = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.bookings) {
      return;
    }
    
    loadingRef.current.bookings = true;
    
    try {
      const response = await apiGet('/.netlify/functions/get-my-bookings');
      

      
      setMyBookings(response.bookings || []);
    } catch (error) {
      console.error('Error fetching my bookings:', error);
      toast.error('Failed to load your bookings');
    } finally {
      loadingRef.current.bookings = false;
      checkInitialDataLoaded();
    }
  };

  // New function to fetch pending tasks
  // Note: Route protection ensures only patients or therapists in demo mode can access this component
  const fetchPendingTasks = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.tasks) {
      return;
    }
    
    loadingRef.current.tasks = true;
    
    try {
      setLoadingTasks(true);
      // Always fetch fresh data from server
      const response = await apiGet('/.netlify/functions/get-my-tasks');
      
      
      if (response.tasks) {
        // Get all tasks, not just pending ones
        setPendingTasks(response.tasks);
      }
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
      // Don't show error toast for tasks as it's not critical
    } finally {
      setLoadingTasks(false);
      loadingRef.current.tasks = false;
      checkInitialDataLoaded();
    }
  };

  // Fetch grounding assignment on page load
  const fetchGroundingAssignment = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.grounding) {
      return;
    }
    
    loadingRef.current.grounding = true;
    
    try {
      setLoadingGrounding(true);
      const response = await groundingService.getAssignment(user);
      if (response.assignment) {
        setGroundingAssignment(response.assignment);
      }
    } catch (error) {
      console.error('Error fetching grounding assignment:', error);
      // Don't show error toast for grounding as it's not critical
    } finally {
      setLoadingGrounding(false);
      loadingRef.current.grounding = false;
    }
  };

  // Fetch goals for the patient
  const fetchGoals = async () => {
    // Prevent duplicate calls
    if (loadingRef.current.goals) {
      return;
    }
    
    // Get patientId from tasks if available (for backward compatibility)
    // If not available, backend will use getEffectivePatientId to determine patient ID
    // This handles demo mode automatically
    let patientId = null;
    
    if (pendingTasks.length > 0) {
      // Extract patientId from first task
      patientId = pendingTasks[0]?.patientId;
    }
    
    loadingRef.current.goals = true;
    
    try {
      setLoadingGoals(true);
      // Pass patientId if available, otherwise let backend use effective patient ID
      // This ensures demo mode works correctly
      const response = await therapeuticGoalsService.getGoals(user, patientId);
      if (response.goals && response.goals.length > 0) {
        setGoals(response.goals);
        // Don't set expandedGoalId here - let the default expansion logic handle it
        // This allows the first goal to expand by default, but also allows collapsing
      } else {
        setGoals([]);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      // Don't show error toast for goals as it's not critical
      setGoals([]);
    } finally {
      setLoadingGoals(false);
      loadingRef.current.goals = false;
    }
  };

  // Generic navigation function for consistent patterns throughout the app
  const navigateToSection = useCallback((newSection) => {
    setPreviousPage(activeSection);  // Remember where we came from
    setActiveSection(newSection);     // Go to new section
  }, [activeSection]);

  // Memoize callbacks for MascotHub to prevent remounts (needed for loading state)
  const handleGroundingClick = useCallback(() => {
    // Close other views when opening grounding
    setShowAppointments(false);
    // Toggle grounding view (if already open, close it; if closed, open it)
    setShowGrounding(prev => !prev);
  }, []);

  const handleMoodComplete = useCallback(async () => {
    // Use existing moodAlreadySubmitted state instead of making another API call
    // The mood status is already checked once on mount and updated when mood is submitted
    if (!moodAlreadySubmitted) {
      // Only show mood tracker if not already submitted today
      setAutoShowMoodTracker(true);
    }
    // If already submitted, mood tracker won't show (handled by moodAlreadySubmitted prop)
  }, [moodAlreadySubmitted]);

  const handleTasksClick = useCallback(() => {
    // Navigate to tasks section
    navigateToSection('tasks');
  }, [navigateToSection]);

  const handleAppointmentsClick = useCallback(() => {
    // Close other views when opening appointments
    setShowGrounding(false);
    // Toggle appointments view (if already open, close it; if closed, open it)
    setShowAppointments(prev => !prev);
  }, []);

  const handleCloseGrounding = useCallback(() => {
    setShowGrounding(false);
    // Don't trigger mood tracker when closing - just close the view
  }, []);

  // Callback when welcome animation completes
  const handleWelcomeComplete = useCallback(() => {
    setWelcomeAnimationComplete(true);
    // Also trigger mood flow logic - if mood wasn't submitted, show mood tracker
    // This ensures the mood tracker and "What do you wanna do?" options appear
    // even when the greeting animation completes during loading
    if (!moodAlreadySubmitted) {
      setAutoShowMoodTracker(true);
    }
  }, [moodAlreadySubmitted]);

  // Check if all initial data has been loaded
  const checkInitialDataLoaded = useCallback(() => {
    // All initial data is loaded when:
    // 1. Therapist is loaded (or failed to load)
    // 2. Bookings are loaded (or failed to load)
    // 3. Tasks are loaded (or failed to load)
    // We check if loading flags are false (meaning the fetch completed)
    if (!loadingRef.current.therapist && !loadingRef.current.bookings && !loadingRef.current.tasks) {
      setInitialDataLoaded(true);
    }
  }, []);

  // Enhanced task rendering with proper action handling - memoized callback
  const renderTaskItem = useCallback((task, onStartTask, onStatusUpdate) => {
    // Get task metadata from registry
    const taskConfig = getTaskById(task.taskTypeId);
    
    return (
      <Card
        key={task._id}
        variant="default"
        padding="small"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-2 flex-1 min-h-[44px]">
            {task.taskTypeId ? (
              <Play className="h-4 w-4 text-indigo-600 mt-0.5" />
            ) : (
              <FileText className="h-4 w-4 text-amber-600 mt-0.5" />
            )}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <p className="font-medium text-gray-900 text-base">
                {taskConfig?.name || task.title || 'Untitled Task'}
              </p>
              {task.dueDate && (
                <p className="text-xs text-gray-500 mt-1">
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                </p>
              )}
              
              {/* Task-specific summary if available */}
              {taskConfig?.component?.Summary && (
                <div className="mt-1">
                  {React.createElement(taskConfig.component.Summary, { task, session: task.session })}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {/* Only show status badge for completed and overdue tasks */}
            {(task.status === 'completed' || task.status === 'overdue') && (
              <Button
                variant={task.status === 'overdue' ? 'error' : 'success'}
                size="small"
                className="p-2"
                disabled
              >
                {task.status === 'overdue' ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 md:hidden" />
                    <span className="hidden md:inline">Completed</span>
                  </>
                )}
              </Button>
            )}
            
            {/* Task Action Buttons - Only show when appropriate */}
            {/* AI Task - Start button */}
            {task.taskTypeId && task.status === 'pending' && (
              <Button
                variant="primary"
                size="small"
                onClick={() => onStartTask(task.taskId)}
                className="p-2"
              >
                <Play className="h-4 w-4" />
              </Button>
            )}

            {/* AI Task - Continue button */}
            {task.taskTypeId && task.status === 'started' && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => onStartTask(task.taskId)}
                className="p-2"
              >
                <Play className="h-4 w-4" />
              </Button>
            )}

            {/* Regular task buttons */}
            {!task.taskTypeId && task.status === 'pending' && (
              <Button
                variant="outline"
                size="small"
                onClick={() => onStatusUpdate(task.taskId, 'started')}
                className="p-2"
              >
                <Play className="h-4 w-4" />
              </Button>
            )}
            
            {!task.taskTypeId && task.status === 'started' && (
              <Button
                variant="success"
                size="small"
                onClick={() => onStatusUpdate(task.taskId, 'completed')}
                className="p-2"
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            )}
            
            {!task.taskTypeId && task.status === 'completed' && (
              <Button
                variant="outline"
                size="small"
                onClick={() => onStatusUpdate(task.taskId, 'pending')}
                className="p-2"
              >
                <Clock className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }, []);

  // Task action handlers - Now properly integrated with the task system
  const handleStartTask = useCallback(async (taskId) => {
    try {
      // Update local state immediately to reflect task start
      setPendingTasks(prevTasks => 
        prevTasks.map(task => 
          task.taskId === taskId 
            ? { ...task, status: 'in_progress' }
            : task
        )
      );
      
      // Remember which section we came from (generic approach)
      setPreviousPage(activeSection);
      
      // Navigate directly to task execution UI
      setCurrentTaskId(taskId);
      
      // Show success message
      toast.success('Task started successfully!');
      
      // Refresh tasks in background to sync with server
      fetchPendingTasks();
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(error.message || 'Failed to start task');
    }
  }, [activeSection]);

  const handleStatusUpdate = useCallback(async (taskId, status) => {
    try {
      // Refresh tasks to show updated status
      fetchPendingTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(error.message || 'Failed to update task status');
    }
  }, []);

  // Memoize computed values for MascotHub
  const computedUserName = useMemo(() => {
    return patientName || userInfo?.name || userInfo?.email?.split('@')[0] || 'there';
  }, [patientName, userInfo?.name, userInfo?.email]);

  const showMoodTrackerValue = useMemo(() => {
    return autoShowMoodTracker && !moodAlreadySubmitted;
  }, [autoShowMoodTracker, moodAlreadySubmitted]);

  const moodTrackerComponentValue = useMemo(() => {
    return autoShowMoodTracker && !moodAlreadySubmitted ? <MoodTracker hideTitle={false} /> : null;
  }, [autoShowMoodTracker, moodAlreadySubmitted]);

  const groundingComponentValue = useMemo(() => {
    return showGrounding ? (
      <GroundingQuickAccess 
        onClose={handleCloseGrounding} 
        assignment={groundingAssignment}
        loading={loadingGrounding}
      />
    ) : null;
  }, [showGrounding, handleCloseGrounding, groundingAssignment, loadingGrounding]);

  // Handle goal click - moved outside useMemo to avoid stale closures
  const handleGoalClick = useCallback((goalId) => {
    setExpandedGoalId(prev => {
      // Toggle: if currently expanded, collapse it; otherwise expand it
      if (prev === goalId) {
        // If clicking the currently expanded goal, collapse it
        return 'collapsed';
      } else if (prev === null && goalId === goals[0]?.goalId) {
        // If clicking the first goal when it's expanded by default (prev === null)
        return 'collapsed';
      } else {
        // If clicking a collapsed goal or different goal, expand it
        return goalId;
      }
    });
  }, [goals]);

  // Memoize goalsComponent to show goals directly in options view
  const goalsComponentValue = useMemo(() => {
    // Group tasks by goalId
    const tasksByGoal = {};
    const tasksWithoutGoal = [];
    
    pendingTasks.forEach(task => {
      const taskGoalId = task.goalId || task.goal_id;
      if (taskGoalId) {
        if (!tasksByGoal[taskGoalId]) {
          tasksByGoal[taskGoalId] = [];
        }
        tasksByGoal[taskGoalId].push(task);
      } else {
        tasksWithoutGoal.push(task);
      }
    });
    
    return (
      <div className="w-full" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
        {loadingTasks || loadingGoals ? (
          <div className="text-center py-3">
            <Spinner size="medium" />
          </div>
        ) : goals.length === 0 && pendingTasks.length === 0 ? (
          <div className="text-center py-6">
            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No goals or tasks assigned</p>
            <p className="text-xs text-gray-400 mt-1">Your therapist will assign goals and tasks as needed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Goals with tasks */}
            {goals.length > 0 && goals.map((goal, index) => {
              const goalTasks = tasksByGoal[goal.goalId] || [];
              // Determine if this goal should be expanded
              // 1. If this goal's ID matches expandedGoalId, it's explicitly expanded
              // 2. If expandedGoalId is null and this is the first goal with tasks, expand by default
              // 3. If expandedGoalId is 'collapsed', don't expand anything by default
              const isExplicitlyExpanded = expandedGoalId === goal.goalId;
              const isDefaultExpanded = expandedGoalId === null && index === 0 && goalTasks.length > 0;
              const isExpanded = isExplicitlyExpanded || isDefaultExpanded;
              
              return (
                <div key={goal.goalId} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGoalClick(goal.goalId);
                    }}
                    className="w-full p-4 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-all duration-150 flex items-center justify-between text-left cursor-pointer"
                    style={{ pointerEvents: 'auto', zIndex: 1 }}
                  >
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-base">{goal.title}</h3>
                        <Badge
                          variant={
                            goal.status === 'achieved' ? 'success' :
                            goal.status === 'in-progress' ? 'accent' : 'default'
                          }
                          size="small"
                        >
                          {goal.status === 'achieved' ? 'Achieved' :
                           goal.status === 'in-progress' ? 'In Progress' : 'Active'}
                        </Badge>
                        {goalTasks.length > 0 && (
                          <span className="text-xs text-gray-500 font-medium">
                            {goalTasks.length} {goalTasks.length === 1 ? 'task' : 'tasks'}
                          </span>
                        )}
                      </div>
                      {goal.description && (
                        <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{goal.description}</p>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      {goalTasks.length > 0 ? (
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No tasks</span>
                      )}
                    </div>
                  </button>
                  
                  {/* Tasks for this goal */}
                  {isExpanded ? (
                    <div className="border-t border-gray-100 bg-white" key={`tasks-${goal.goalId}-${isExpanded}`}>
                      {goalTasks.length > 0 ? (
                        <div className="p-3 space-y-2">
                          {goalTasks.map((task) => (
                            <React.Fragment key={task.taskId || task._id}>
                              {renderTaskItem(
                                task, 
                                (taskId) => {
                                  handleStartTask(taskId);
                                },
                                (taskId, status) => {
                                  handleStatusUpdate(taskId, status);
                                }
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                          No tasks assigned to this goal yet
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
            
            {/* Tasks without a goal */}
            {tasksWithoutGoal.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOtherTasksExpanded(prev => !prev);
                  }}
                  className="w-full p-4 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-all duration-150 flex items-center justify-between text-left cursor-pointer"
                  style={{ pointerEvents: 'auto', zIndex: 1 }}
                >
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="font-semibold text-gray-900 text-base">Other Tasks</h3>
                      <span className="text-xs text-gray-500 font-medium">
                        {tasksWithoutGoal.length} {tasksWithoutGoal.length === 1 ? 'task' : 'tasks'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Tasks not linked to a goal
                    </p>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    <div className={`transition-transform duration-200 ${otherTasksExpanded ? 'rotate-180' : ''}`}>
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </button>
                
                {/* Tasks for other tasks section */}
                {otherTasksExpanded ? (
                  <div className="border-t border-gray-100 bg-white">
                    <div className="p-3 space-y-2">
                      {tasksWithoutGoal.map((task) => (
                        <React.Fragment key={task.taskId || task._id}>
                          {renderTaskItem(
                            task, 
                            (taskId) => {
                              handleStartTask(taskId);
                            },
                            (taskId, status) => {
                              handleStatusUpdate(taskId, status);
                            }
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [loadingTasks, loadingGoals, pendingTasks, goals, expandedGoalId, otherTasksExpanded, handleStartTask, handleStatusUpdate, renderTaskItem, handleGoalClick]);

  // Memoize Dashboard Content to prevent MascotHub remounts
  // Must be defined after all callbacks are created
  const dashboardContentMemo = useMemo(() => (
    <div className="space-y-3 md:space-y-4 h-full flex flex-col">
      
      {/* Mascot Hub - Main Hero Element */}
      <div className="flex-1 flex flex-col min-h-0">
        <MascotHub
          key="mascot-hub-stable"
          onGroundingClick={handleGroundingClick}
          onMoodComplete={handleMoodComplete}
          onTasksClick={handleTasksClick}
          onAppointmentsClick={handleAppointmentsClick}
          userName={computedUserName}
          showMoodTracker={showMoodTrackerValue}
          moodTrackerComponent={moodTrackerComponentValue}
          moodAlreadySubmitted={moodAlreadySubmitted}
          showGrounding={showGrounding}
          groundingComponent={groundingComponentValue}
          onCloseGrounding={handleCloseGrounding}
          goalsComponent={goalsComponentValue}
        />
      </div>

      {/* Upcoming Appointments Section - Shown when appointments icon is clicked */}
      {showAppointments && (
        <Card variant="default" padding="large">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Upcoming Appointments</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="small"
                onClick={() => navigateToSection('appointments')}
                className="flex items-center text-sm text-indigo-700 hover:text-indigo-800 font-medium"
              >
                View All
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setShowAppointments(false)}
                className="p-1"
                title="Hide Appointments"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {(() => {
            const now = Math.floor(Date.now() / 1000);
            const upcomingBookings = myBookings
              .filter(booking => booking.startEpoch > now)
              .sort((a, b) => a.startEpoch - b.startEpoch)
              .slice(0, 2); // Show only next 2 appointments
            
            if (upcomingBookings.length === 0) {
              return (
                <div className="text-center py-6">
                  <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No upcoming appointments</p>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setShowBookingModal(true)}
                    className="mt-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    Book your first appointment
                  </Button>
                </div>
              );
            }
            
            return (
              <div className="space-y-2">
                {upcomingBookings.map((booking) => (
                  <Card
                    key={booking._id}
                    variant="default"
                    padding="small"
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-indigo-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {convertEpochToLocalDate(booking.startEpoch)}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatTimeRange(booking.startEpoch, booking.endEpoch)}
                        </p>
                        {booking.meetLink && (
                          <a
                            href={booking.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-1"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Join Meet
                          </a>
                        )}
                      </div>
                    </div>
                    <Badge variant="success" size="small">
                      Confirmed
                    </Badge>
                  </Card>
                ))}
              </div>
            );
          })()}
          
          {/* Book Appointment Button */}
          <div className="mt-3">
            <Divider />
            <Button
              variant="primary"
              size="medium"
              onClick={() => setShowBookingModal(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Book Appointment
            </Button>
          </div>
        </Card>
      )}
    </div>
  ), [
    hasNotificationSubscription,
    handleGroundingClick,
    handleMoodComplete,
    handleTasksClick,
    handleAppointmentsClick,
    computedUserName,
    showMoodTrackerValue,
    moodTrackerComponentValue,
    moodAlreadySubmitted,
    showGrounding,
    groundingComponentValue,
    handleCloseGrounding,
    goalsComponentValue,
    showAppointments,
    myBookings,
    setShowBookingModal,
    navigateToSection,
    setShowAppointments,
    handleStartTask,
    handleStatusUpdate
  ]);

  // Memoize section content to prevent unnecessary re-renders
  // Must be defined after dashboardContentMemo is created
  const sectionContent = useMemo(() => {
    switch (activeSection) {
      case 'dashboard':
        return dashboardContentMemo;
      case 'appointments':
        return <AppointmentsContent />;
      case 'tasks':
        return <PatientTasks onStartTask={handleStartTask} onStatusUpdate={handleStatusUpdate} />;
      case 'cards':
        return <CardCollectionView />;
      case 'analytics':
        return <AnalyticsContent />;
      case 'therapist':
        return <TherapistInfoContent />;
      case 'settings':
        return <SettingsContent />;
      case 'notifications':
        return <NotificationsContent />;
      case 'help':
        return <HelpContent />;
      default:
        return dashboardContentMemo;
    }
  }, [activeSection, dashboardContentMemo, handleStartTask, handleStatusUpdate]);

  const handleBackToDashboard = useCallback(() => {
    setCurrentTaskId(null);
    // Navigate back to the previous page (generic approach)
    setActiveSection(previousPage);
    fetchPendingTasks(); // Refresh tasks to show updated status
  }, [previousPage]);

  const handleViewCards = useCallback(() => {
    setCurrentTaskId(null);
    setActiveSection('cards');
  }, []);

  const handleHelpClick = useCallback((section) => {
    setHelpSection(section);
    setShowHelp(true);
  }, []);

  const closeHelp = useCallback(() => {
    setShowHelp(false);
    setHelpSection('');
  }, []);

  // Consolidated initialization effect - runs once on mount
  useEffect(() => {
    // Prevent duplicate initialization (especially from StrictMode)
    if (hasInitializedRef.current) {
      return;
    }
    
    hasInitializedRef.current = true;
    
    // Route protection ensures only patients or therapists in demo mode can access
    // userInfo is guaranteed to be loaded before component renders (route protection)
    
    // Initialize all data in parallel (fetch functions have their own loading guards)
    fetchAssignedTherapist();
    fetchMyBookings();
    fetchPendingTasks();
    fetchGroundingAssignment();
  }, []); // Run once on mount - userInfo is already loaded via route protection

  // Fetch goals when tasks are loaded (or when tasks array changes)
  // Note: fetchGoals now works even when there are no tasks (uses effective patient ID)
  useEffect(() => {
    // Fetch goals after a short delay to ensure tasks have been processed
    // This handles both cases: when tasks exist and when they don't
    const timer = setTimeout(() => {
      fetchGoals();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [pendingTasks.length]); // Fetch when tasks array length changes


  // Refresh tasks when returning to dashboard (but not on initial mount)
  // Route protection ensures only authorized users can access this component
  useEffect(() => {
    // Skip if this is the initial mount - set ref and return without doing anything
    if (!hasInitializedRef.current) {
      previousActiveSectionRef.current = activeSection;
      return;
    }
    
    // Only refresh if we're actually returning TO dashboard from a different section
    // This prevents refresh on initial mount when activeSection is already 'dashboard'
    const previousSection = previousActiveSectionRef.current;
    if (activeSection === 'dashboard' && previousSection !== 'dashboard' && previousSection !== null) {
      fetchPendingTasks();
    }
    
    // Always update previous section reference (but only after initialization)
    previousActiveSectionRef.current = activeSection;
  }, [activeSection]);

  // Fetch available slots when therapist is assigned or date changes
  useEffect(() => {
    if (assignedTherapist) {
      fetchAvailableSlots();
    }
  }, [selectedDate, assignedTherapist]);

  const onSubmit = async (data) => {
    if (!assignedTherapist) {
      toast.error('No therapist assigned. Please contact your administrator.');
      return;
    }

    try {
      setLoading(true);

      // Calculate duration from start and end times
      const startParts = data.startTime.split(':');
      const endParts = data.endTime.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      
      let durationMinutes = endMinutes - startMinutes;
      if (durationMinutes < 0) {
        durationMinutes += 24 * 60; // Handle overnight bookings
      }
      
      if (durationMinutes <= 0) {
        toast.error('End time must be after start time');
        return;
      }

      // Convert 12-hour format to 24-hour format for API calls
      const startTime24Hour = convert12HourTo24Hour(data.startTime);
      const endTime24Hour = convert12HourTo24Hour(data.endTime);
      
      const startEpoch = convertLocalTimeToEpoch(startTime24Hour, selectedDate);
      const endEpoch = convertLocalTimeToEpoch(endTime24Hour, selectedDate);

      // Note: Backend validation will handle slot availability checks
      // The backend checks if the requested time falls within therapist's availability
      // and ensures no conflicts with existing bookings

      const bookingData = {
        therapistId: assignedTherapist.userId, // Use userId consistently
        startTime: startEpoch,
        endTime: endEpoch,
        duration: durationMinutes,
        message: data.message || '',
        date: format(selectedDate, 'yyyy-MM-dd')
      };

      const response = await apiPost('/.netlify/functions/book-slot', bookingData);
      toast.success('Booking confirmed successfully!');
      reset();
      setShowBookingForm(false);
      setSelectedSlot(null);
      
      // If we came from the booking modal, close it too
      if (cameFromBookingModal) {
        setShowBookingModal(false);
        setCameFromBookingModal(false);
      }
      
      fetchAvailableSlots();
      fetchMyBookings();
    } catch (error) {
      console.error('Error booking slot:', error);
      toast.error(error.message || 'Failed to book slot');
    } finally {
      setLoading(false);
    }
  };

  // Memoize calendar days generation to prevent recalculation on every render
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days to align with calendar grid
    const startDay = start.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const paddingStart = Array.from({ length: startDay }, (_, i) => null);
    
    return [...paddingStart, ...days];
  }, [currentMonth]);

  // Memoize past date check
  const isPastDate = useCallback((date) => {
    return isBefore(date, startOfToday());
  }, []);

  const handleMonthChange = useCallback((direction) => {
    if (direction === 'next') {
      setCurrentMonth(addMonths(currentMonth, 1));
    } else {
      setCurrentMonth(subMonths(currentMonth, 1));
    }
  }, [currentMonth]);

  // Show welcome animation while loading initial data, waiting for animation, or if therapist info is still loading
  // Wait for BOTH data to load AND animation to complete before showing dashboard
  if (!initialDataLoaded || !welcomeAnimationComplete || loadingTherapist) {
    // Show welcome animation during loading - don't show spinner
    return (
      <div className="h-screen" style={{backgroundColor: 'var(--color-background)'}}>
        <div className="flex h-full">
          <div className="flex-1 flex flex-col h-full min-w-0">
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-3 md:p-6 h-full flex flex-col">
                <div className="space-y-3 md:space-y-4 h-full flex flex-col">
                  {/* Show welcome animation during loading */}
                  <div className="flex-1 flex flex-col min-h-0">
                    <MascotHub
                      key="mascot-hub-loading"
                      onGroundingClick={handleGroundingClick}
                      onMoodComplete={handleWelcomeComplete}
                      onTasksClick={handleTasksClick}
                      onAppointmentsClick={handleAppointmentsClick}
                      userName={computedUserName}
                      showMoodTracker={false}
                      moodTrackerComponent={null}
                      moodAlreadySubmitted={moodAlreadySubmitted}
                      showGrounding={false}
                      groundingComponent={null}
                      onCloseGrounding={handleCloseGrounding}
                      goalsComponent={null}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assignedTherapist) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: 'var(--color-background)'}}>
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Therapist Assigned</h2>
          <p className="text-gray-600 mb-4">You don't have a therapist assigned to your account.</p>
          <p className="text-sm text-gray-500">Please contact your administrator to get assigned to a therapist.</p>
        </div>
      </div>
    );
  }

  // If we're viewing a specific task, show the task page
  if (currentTaskId) {
    return (
      <div className="h-screen" style={{backgroundColor: 'var(--color-background)'}}>
        <TaskPage 
          taskId={currentTaskId} 
          onBack={handleBackToDashboard}
          onViewCards={handleViewCards}
        />
      </div>
    );
  }


  return (
    <div className="h-screen" style={{backgroundColor: 'var(--color-background)'}}>
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="flex h-full">
        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar Navigation */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-sm border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-4 md:p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-6 lg:mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 md:h-6 md:w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">Patient Portal</h2>
                  <p className="text-xs md:text-sm text-gray-500">Welcome back</p>
                </div>
              </div>
              <CloseButton
                onClick={() => setSidebarOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700"
              />
            </div>

            {/* Navigation Menu - Scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <Navigation variant="vertical">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <NavigationItem
                      key={item.id}
                      active={activeSection === item.id}
                      onClick={() => {
                        navigateToSection(item.id);
                        setSidebarOpen(false);
                      }}
                      icon={<IconComponent className="h-4 w-4 md:h-5 md:w-5" />}
                    >
                      {item.name}
                    </NavigationItem>
                  );
                })}
              </Navigation>
            </div>

            {/* Logout Button - Fixed at bottom */}
            <div className="mt-4 md:mt-8 pt-4 md:pt-6 border-t border-gray-200 flex-shrink-0">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start"
              >
                <LogOut className="h-4 w-4 md:h-5 md:w-5 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full min-w-0">
          {/* Header */}
          <DynamicHeader
            activeSection={activeSection}
            onMenuClick={() => setSidebarOpen(true)}
            onBackClick={() => navigateToSection('dashboard')}
            onHelpClick={() => handleHelpClick(activeSection)}
            sectionNames={{
              appointments: 'Appointments',
              tasks: 'My Tasks',
              cards: 'My Cards',
              analytics: 'Analytics',
              therapist: 'About Therapist',
              settings: 'Settings',
              notifications: 'Notifications',
              help: 'Help & Support'
            }}
            appName="TheraEngage"
            showHelpIcon={activeSection !== 'dashboard'}
          />


          {/* Page Content */}
          <div className={`flex-1 min-h-0 ${activeSection === 'dashboard' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            <div className={`${activeSection === 'dashboard' ? 'h-full' : ''} p-3 md:p-6 flex flex-col`}>
            {sectionContent}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal - Slot Selection */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <Card variant="default" padding="large" className="relative top-4 md:top-20 mx-auto w-11/12 md:w-4/5 lg:w-3/5">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Book Appointment</h3>
                <CloseButton
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedSlot(null);
                  }}
                />
              </div>
              
              {/* Date Navigation */}
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Button
                  onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                  variant="ghost"
                  size="small"
                  className="p-2"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                
                <Button
                  onClick={() => setShowDatePicker(true)}
                  variant="ghost"
                  size="medium"
                  className="px-4 py-2 text-lg font-medium text-gray-900 hover:text-indigo-600"
                >
                  {format(selectedDate, 'MMM d yyyy')}
                </Button>
                
                <Button
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                  variant="ghost"
                  size="small"
                  className="p-2"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              {/* Available Slots */}
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
                  <h4 className="text-md font-medium text-gray-900">
                    Available Slots
                  </h4>
                  <div className="text-sm text-gray-500">
                    {availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available
                  </div>
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <Spinner size="medium" />
                    <p className="text-gray-500 mt-2">Loading available slots...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No available slots for this date</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {availableSlots.map((slot, index) => {
                      const isSelected = selectedSlot && 
                        selectedSlot.startEpoch === slot.startEpoch && 
                        selectedSlot.endEpoch === slot.endEpoch;
                      
                      return (
                        <Card
                          key={index}
                          variant={isSelected ? "default" : "outlined"}
                          padding="small"
                          className={`cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors duration-200 text-left ${
                            isSelected ? 'border-indigo-500 bg-indigo-50' : ''
                          }`}
                          onClick={() => {
                            setSelectedSlot(slot);
                            setCameFromBookingModal(true);
                            setShowBookingForm(true);
                            setShowBookingModal(false);
                          }}
                        >
                          <p className="font-medium text-gray-900">
                            {convertEpochToLocalTime(slot.startEpoch)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {calculateDuration(slot.startEpoch, slot.endEpoch)}
                          </p>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <Card variant="default" padding="large" className="relative top-4 md:top-20 mx-auto w-11/12 md:w-96">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Book Appointment</h3>
                <CloseButton
                  onClick={() => {
                    setShowBookingForm(false);
                    setSelectedSlot(null);
                    reset();
                    // If we came from the booking modal, go back to it
                    if (cameFromBookingModal) {
                      setShowBookingModal(true);
                      setCameFromBookingModal(false);
                    }
                  }}
                />
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Select
                      label="Start Time"
                      placeholder="Select start time"
                      value={startTime || ''}
                      onChange={(e) => setValue('startTime', e.target.value, { shouldValidate: true })}
                      error={!!errors.startTime}
                      errorText={errors.startTime?.message}
                      options={timeOptions.map(time => ({ value: time, label: time }))}
                    />
                  </div>

                  <div>
                    <Select
                      label="End Time"
                      placeholder="Select end time"
                      value={endTime || ''}
                      onChange={(e) => setValue('endTime', e.target.value, { shouldValidate: true })}
                      error={!!errors.endTime}
                      errorText={errors.endTime?.message}
                      options={timeOptions.map(time => ({ value: time, label: time }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700">
                    <span>{calculateDurationFromTimes(startTime, endTime)}</span>
                  </div>
                </div>

                <div>
                  <Input
                    label="Message (optional)"
                    {...register('message')}
                    type="textarea"
                    rows={3}
                    placeholder="Any special requests or notes..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <Button
                    type="submit"
                    variant="primary"
                    size="medium"
                    disabled={loading}
                    className="flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {loading ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="medium"
                    onClick={() => {
                      setShowBookingForm(false);
                      setSelectedSlot(null);
                      reset();
                      // If we came from the booking modal, go back to it
                      if (cameFromBookingModal) {
                        setShowBookingModal(true);
                        setCameFromBookingModal(false);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <Card variant="default" padding="large" className="relative top-4 md:top-20 mx-auto w-11/12 md:w-80">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Select Date</h3>
                <CloseButton onClick={() => setShowDatePicker(false)} />
              </div>
              
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  onClick={() => handleMonthChange('prev')}
                  variant="ghost"
                  size="small"
                  className="p-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h4 className="text-lg font-medium text-gray-900">
                  {format(currentMonth, 'MMMM yyyy')}
                </h4>
                <Button
                  onClick={() => handleMonthChange('next')}
                  variant="ghost"
                  size="small"
                  className="p-2"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-xs text-gray-500 text-center py-2 font-medium">
                    {day}
                  </div>
                ))}
                {calendarDays.map((date, index) => (
                  <div key={index} className="p-1">
                    {date ? (
                      <Button
                        onClick={() => {
                          setSelectedDate(date);
                          setShowDatePicker(false);
                        }}
                        disabled={isPastDate(date)}
                        variant={format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') ? 'primary' : 'ghost'}
                        size="small"
                        className={`w-full p-2 text-sm ${
                          format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                            ? 'bg-indigo-600 text-white'
                            : isPastDate(date)
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {format(date, 'd')}
                      </Button>
                    ) : (
                      <div className="p-2"></div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => setShowDatePicker(false)}
                  variant="outline"
                  size="small"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card variant="default" padding="none" className="max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {helpSection === 'appointments' ? 'Appointments Help' :
                 helpSection === 'tasks' ? 'Tasks Help' :
                 helpSection === 'chat' ? 'AI Chat Help' : 'Help'}
              </h3>
              <CloseButton onClick={closeHelp} />
            </div>
            <div className="p-4">
              {helpSection === 'appointments' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Manage your therapy appointments here. You can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>View available time slots</li>
                    <li>Book new appointments</li>
                    <li>See your upcoming appointments</li>
                    <li>Cancel or reschedule if needed</li>
                  </ul>
                </div>
              )}
              
              {helpSection === 'tasks' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Track and complete your assigned tasks here. You can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>View all assigned tasks</li>
                    <li>Start or continue tasks</li>
                    <li>Mark tasks as complete</li>
                    <li>Track your progress</li>
                  </ul>
                </div>
              )}
              
              {helpSection === 'chat' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Chat with our AI assistant for support and guidance. You can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Ask questions about your therapy</li>
                    <li>Get support between sessions</li>
                    <li>Access helpful resources</li>
                    <li>Start new conversations</li>
                  </ul>
                </div>
              )}
              
              {!['appointments', 'tasks', 'chat'].includes(helpSection) && (
                <p className="text-sm text-gray-600">
                  Help content for this section is coming soon.
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Notification Prompt Modal - Auto-shows in PWA mode */}
      <NotificationPromptModal
        userId={userInfo?.userId}
        onSubscriptionChange={(subscription) => {
          setHasNotificationSubscription(!!subscription);
        }}
      />
    </div>
  );
};

export default PatientDashboard;